import { computed, onMounted, ref, type ComputedRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { amplitudeExperiment } from 'freedom-shared';

import { api } from '@/shared/api';
import { sentryModel } from '@/shared/model';

import type { VisaFlowVariant } from '../types';
import {
  readForcedVisaFlowVariant,
  resolveVisaFlowVariant,
  VISA_CHAT_FLOW_VARIANT,
  VISA_WIZARD_FLOW_VARIANT,
} from './visa-flow-variant';
import { resolveEntryCountry, type VisaEntryOrder } from './visa-country';

interface OrdersPage {
  results: VisaEntryOrder[];
}

const fetchActiveOrders = (): Promise<VisaEntryOrder[]> =>
  api.client
    .get<OrdersPage>('accounts/api/v2/orders/?tab=active&show_b2b=true')
    .then(({ data }) => data.results);

/**
 * Вход из заказа: кабинет страну не резолвит — реестр живёт только в авиа,
 * иначе умирает критерий «третья страна — одна запись». Витрина сама находит
 * заказ и уходит на страницу страны через `replace`, не оставляя себя в
 * истории. Страна не нашлась — остаёмся на витрине.
 */
export const useVisaEntry = () => {
  const route = useRoute();
  const router = useRouter();

  const entryOrderId =
    route.query.src === 'order' && typeof route.query.orderId === 'string'
      ? route.query.orderId
      : null;

  const isResolvingEntry = ref(entryOrderId !== null);

  onMounted(async () => {
    if (entryOrderId === null) {
      return;
    }

    try {
      const orders = await fetchActiveOrders();
      const country = resolveEntryCountry(orders, entryOrderId);

      if (country) {
        await router.replace({
          path: `/visa/${country.code}`,
          query: route.query,
        });
        return;
      }
    } catch (error) {
      sentryModel.captureException(
        sentryModel.getReadableError(
          sentryModel.createCustomErrorText(
            sentryModel.errorPrefix.DEFAULT,
            'ERR_VISA_ENTRY_ORDERS_FETCH',
          ),
          error instanceof Error ? error : new Error(String(error)),
        ),
      );
    }

    isResolvingEntry.value = false;
  });

  return { isResolvingEntry };
};

/**
 * Вариант флоу по release-флагу: `chat` — сбор диалогом бота, `wizard` — те же
 * шаги без чата, `null` — раздел выключен. Один эксперимент, а не два флага:
 * варианты взаимоисключающие и сравниваются между собой. Гейтится вход, а не
 * роуты — прямой URL остаётся рабочим для QA и диплинков. Резолв в фиче, чтобы
 * точки входа не повторяли ни флаг, ни DEV-обход.
 */
export const useVisaFlowVariant = (): {
  visaFlowVariant: ComputedRef<VisaFlowVariant | null>;
} => {
  const { isVariant: isChatFlow } =
    amplitudeExperiment.lib.checkExperimentVariant(
      amplitudeExperiment.config.flags.VISA_HELP,
      VISA_CHAT_FLOW_VARIANT,
    );
  const { isVariant: isWizardFlow } =
    amplitudeExperiment.lib.checkExperimentVariant(
      amplitudeExperiment.config.flags.VISA_HELP,
      VISA_WIZARD_FLOW_VARIANT,
    );

  const visaFlowVariant = computed(() =>
    resolveVisaFlowVariant({
      isChatFlow: isChatFlow.value,
      isWizardFlow: isWizardFlow.value,
      forcedVariant: import.meta.env.DEV ? readForcedVisaFlowVariant() : null,
    }),
  );

  return { visaFlowVariant };
};

/** Условие показа точек входа: раздел включён любым из двух вариантов. */
export const useVisaHelpEntry = (): {
  isVisaEntryVisible: ComputedRef<boolean>;
} => {
  const { visaFlowVariant } = useVisaFlowVariant();

  return {
    isVisaEntryVisible: computed(() => visaFlowVariant.value !== null),
  };
};
